import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CONTENT_CAP_PADDED, OVERLAY_CAP } from './large-screen';

const CLAMP_STYLE = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: 2,
    overflow: 'hidden',
};

export interface DetailHeroProps {
    /** Parenthesised eyebrow, e.g. "(Cult Design)". */
    eyebrow?: string;
    /** When set, the eyebrow links here (e.g. the professional's profile). */
    eyebrowLink?: string;
    title: string;
    /**
     * Single full-bleed background image (project). For the professional hero,
     * pass {@link backgroundImages} instead to get a cross-fade carousel. When
     * neither is set, renders the text hero offset to the right of the
     * unified-header logomark (product).
     */
    backgroundImage?: string;
    /** Multiple background images — cross-fades between them (professional hero). */
    backgroundImages?: string[];
    /** Logo badge for the image-hero variant. `link` makes the badge a profile link. */
    logo?: { src: string; background?: string | null; link?: string };
    /**
     * Image-hero geometry overrides (merged over the defaults via `cn`, so a
     * page only passes the dimensions that differ). Used where a page's hero is
     * a different height or sits the title elsewhere than the default
     * bottom-left treatment.
     */
    aspectClassName?: string;
    contentClassName?: string;
    titleClassName?: string;
    /** Override the logo badge geometry (e.g. its right offset / alignment). */
    logoClassName?: string;
    /**
     * Replace the bottom legibility scrim entirely. Pages whose hero sits above
     * a dark section pass a fade that reaches that section's colour at the very
     * bottom so the hero blends seamlessly into it (e.g. the project page).
     */
    scrimClassName?: string;
    /**
     * Client-toggled darkening overlay (the banner's Overlay setting in the
     * BM). Renders a uniform black/20 wash over the whole image, under the
     * legibility scrims.
     */
    overlay?: boolean;
    /**
     * Optional second scrim over the hero's top edge — a subtle fade that
     * keeps the transparent top nav legible over bright imagery. Rendered
     * only when provided.
     */
    topScrimClassName?: string;
    /**
     * View-transition name claim for the clipped banner box, so a listing
     * tile can morph into it (`useTileHeroViewTransitionStyle`). Applied to
     * the background div rather than the section: the overlaid title and logo
     * badge then ride the root cross-fade instead of flying inside the box.
     */
    heroStyle?: CSSProperties;
    /**
     * The already-decoded tile rendition the user clicked, painted under the
     * banner while the full-size rendition loads — a cold cache shows the
     * same pixels sharpening instead of the dark placeholder slab.
     */
    heroUnderlaySrc?: string | null;
    /**
     * Text-hero variant only: renders the title at the compact scale so the
     * content below (the product gallery) sits higher in the fold.
     */
    compact?: boolean;
    /**
     * A small rendition of the first banner painted as the base layer on
     * every load (not just transition arrivals), so the hero shows the image
     * near-instantly and the full-size rendition sharpens over it.
     */
    heroBaseSrc?: string | null;
}

/** Default bottom-up scrim — backs the overlaid title over bright banners. */
const DEFAULT_SCRIM =
    'bg-linear-to-t absolute inset-x-0 bottom-0 h-[62%] from-[#131313] via-[#131313]/55 to-transparent opacity-[0.9]';

/**
 * Full-bleed background that cross-fades between images (professional banner
 * carousel). A single image renders statically; reduced-motion holds the first
 * frame. Shared with the mobile social-profile hero.
 */
export const HeroCrossfade = ({ images }: { images: string[] }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (images.length < 2) return;
        const reduce = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (reduce) return;
        const id = setInterval(
            () => setIndex((i) => (i + 1) % images.length),
            5000,
        );
        return () => clearInterval(id);
    }, [images.length]);

    return (
        <>
            {images.map((src, i) => (
                <img
                    key={`${src}-${i}`}
                    src={src}
                    alt=""
                    aria-hidden="true"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className="absolute inset-0 size-full object-cover transition-opacity duration-1000 ease-in-out"
                    style={{ opacity: i === index ? 1 : 0 }}
                />
            ))}
        </>
    );
};

/**
 * Shared detail-page hero.
 *
 * - **Text** (product): brand eyebrow + the large grotesk title, offset to the
 *   right of the header logomark and lifted so the eyebrow lines up with the
 *   bottom of the "A". The title clamps to two lines and expands on click when
 *   it overflows (long-title edge case).
 * - **Image** (project / professional): a full-bleed background (single image,
 *   or a cross-fade carousel via {@link backgroundImages}) with the eyebrow +
 *   title overlaid bottom-left and an optional logo badge top-right.
 */
export const DetailHero = ({
    eyebrow,
    eyebrowLink,
    title,
    backgroundImage,
    backgroundImages,
    logo,
    aspectClassName,
    contentClassName,
    titleClassName,
    logoClassName,
    scrimClassName,
    topScrimClassName,
    overlay = false,
    heroStyle,
    heroUnderlaySrc,
    heroBaseSrc,
    compact = false,
}: DetailHeroProps) => {
    const images =
        backgroundImages ?? (backgroundImage ? [backgroundImage] : []);
    const isImageHero = images.length > 0;

    const renderEyebrow = (colorClass: string) => {
        if (!eyebrow) return null;
        const label = (
            <SectionLabel className={colorClass}>{eyebrow}</SectionLabel>
        );
        return eyebrowLink ? (
            <RoccoLink
                to={eyebrowLink}
                className="inline-block w-fit transition-opacity hover:opacity-70"
            >
                {label}
            </RoccoLink>
        ) : (
            label
        );
    };
    const titleRef = useRef<HTMLHeadingElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    const expandedRef = useRef(false);
    useEffect(() => {
        expandedRef.current = expanded;
    }, [expanded]);

    useEffect(() => {
        const el = titleRef.current;
        if (!el || isImageHero) return;
        const measure = () => {
            if (expandedRef.current) return;
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 1;
            setOverflows(Math.round(el.scrollHeight / lineHeight) > 2);
        };
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [title, isImageHero]);

    if (isImageHero) {
        return (
            <section
                // Named so the hero is a real `region` landmark — on the
                // professional pages it mounts in the persistent layout,
                // outside the tab's `<main>`.
                aria-label={title}
                className={cn(
                    // md:max-h holds the banner at its 1920 design height on
                    // wider screens (the aspect box would keep growing) — the
                    // background crops wider via object-cover instead. Pages
                    // overriding the aspect pass a matching max-h.
                    'relative w-full text-white max-md:aspect-[414/520] md:aspect-[1920/1373] md:max-h-[85.8125rem]',
                    aspectClassName,
                )}
            >
                {/* Clipped background — cross-fade carousel + bottom gradient. */}
                <div
                    className="bg-neutral-900 absolute inset-0 overflow-hidden"
                    style={heroStyle}
                >
                    {heroBaseSrc ? (
                        <img
                            src={heroBaseSrc}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 size-full object-cover"
                        />
                    ) : null}
                    {heroUnderlaySrc ? (
                        <img
                            src={heroUnderlaySrc}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 size-full object-cover"
                        />
                    ) : null}
                    <HeroCrossfade images={images} />
                    {/* Client-toggled darkening wash over the whole image,
                        under the legibility scrims. */}
                    {overlay ? (
                        <div className="absolute inset-0 bg-black/20" />
                    ) : null}
                    {/* Bottom legibility scrim; pages above a dark section pass
                        a scrimClassName that fades to that colour so the hero's
                        lower edge blends seamlessly into it. */}
                    <div className={scrimClassName ?? DEFAULT_SCRIM} />
                    {topScrimClassName ? (
                        <div className={topScrimClassName} />
                    ) : null}
                </div>
                {/* Overlay content anchors to the centred 1920 container, not
                    the full-bleed banner, so the title and badge keep their
                    design offsets on wider screens. */}
                <div className={OVERLAY_CAP}>
                    {/* Brand badge straddles the bottom edge of the banner. */}
                    {logo ? (
                        logo.link ? (
                            <RoccoLink
                                to={logo.link}
                                className={cn(
                                    'detail-hero-logo-vt absolute z-10 block size-16 max-md:right-6 max-md:bottom-6 md:right-[7.68rem] md:-bottom-[5.4rem] md:size-[10.8rem]',
                                    logoClassName,
                                )}
                            >
                                <ProfessionalTileLogo
                                    image={{
                                        src: logo.src,
                                        alt: `${title} logo`,
                                    }}
                                    background={logo.background ?? null}
                                    className="size-full"
                                    contrastBorder
                                />
                            </RoccoLink>
                        ) : (
                            <ProfessionalTileLogo
                                image={{ src: logo.src, alt: `${title} logo` }}
                                background={logo.background ?? null}
                                className={cn(
                                    'detail-hero-logo-vt absolute z-10 size-16 max-md:right-6 max-md:bottom-6 md:right-[7.68rem] md:-bottom-[5.4rem] md:size-[10.8rem]',
                                    logoClassName,
                                )}
                                contrastBorder
                            />
                        )
                    ) : null}
                    {/* Title — anchored 52% down (Figma 714/1372.789); pages may
                        override the geometry via the *ClassName props. */}
                    <div
                        className={cn(
                            'detail-hero-title-vt absolute max-md:right-6 max-md:bottom-6 max-md:left-6 md:top-[52%] md:left-[3.55rem] md:max-w-[90rem]',
                            contentClassName,
                        )}
                    >
                        {renderEyebrow('text-white/80')}
                        <h1
                            className={cn(
                                'm-0 font-sans font-normal text-[#e5e5e5] max-md:mt-2 max-md:text-[2.3287rem] max-md:leading-[1.02] md:mt-[0.9rem] md:text-[7.5rem] md:leading-[1.08] md:tracking-[-0.3rem]',
                                titleClassName,
                            )}
                        >
                            {title}
                        </h1>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-white max-md:px-6 max-md:pt-8 md:px-[3rem] md:pt-[0.6rem]">
            <div className={CONTENT_CAP_PADDED}>
                <div className="md:relative md:-top-[2.4rem] md:z-[130] md:-mb-[2.4rem] md:ml-[28.8rem]">
                    {/* Eyebrow shares the title ink (Figma product title 2354:58896),
                    not a separate grey. */}
                    {renderEyebrow('text-[#181818]')}
                    <h1
                        ref={titleRef}
                        onClick={
                            overflows ? () => setExpanded((v) => !v) : undefined
                        }
                        style={overflows && !expanded ? CLAMP_STYLE : undefined}
                        className={cn(
                            // Title: Figma product hero (node 2354:58896) — 100px
                            // (6.25rem @1920), #343434, tracking -2px.
                            'm-0 font-sans font-normal text-[#343434] max-md:mt-3 md:mt-[0.9rem] md:max-w-[81rem] md:tracking-[-0.125rem]',
                            overflows && 'cursor-pointer',
                            expanded || compact
                                ? 'max-md:text-[1.5525rem] max-md:leading-[1.1] md:text-[3.9rem] md:leading-[1.08]'
                                : 'max-md:text-[2.07rem] max-md:leading-[1.06] md:text-[6.25rem] md:leading-[1.0]',
                        )}
                    >
                        {title}
                    </h1>
                </div>
            </div>
        </section>
    );
};
